/**
 * Lightweight Google Drive search + download helpers.
 *
 * Unlike `googleDrivePicker.ts` which opens Google's heavyweight Picker UI,
 * this module performs a typed search via the Drive REST API and returns
 * results that can be rendered inside our own UI.
 *
 * Token caching: we keep the OAuth access token in sessionStorage for the
 * lifetime of the tab (~1h) so subsequent searches skip the consent popup.
 */

declare global {
  interface Window {
    google: any;
  }
}

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

const SCOPES = "https://www.googleapis.com/auth/drive.readonly";
const TOKEN_KEY = "gdrive_access_token";
const TOKEN_EXPIRY_KEY = "gdrive_access_token_expires_at";

export const PDF_MIME = "application/pdf";
export const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime?: string;
  size?: string;
  iconLink?: string;
}

let gisReadyPromise: Promise<void> | null = null;

function waitForGlobal(check: () => boolean, timeoutMs = 10000): Promise<void> {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const tick = () => {
      if (check()) return resolve();
      if (Date.now() - start > timeoutMs) {
        return reject(new Error("Timed out waiting for Google scripts to load"));
      }
      setTimeout(tick, 50);
    };
    tick();
  });
}

function ensureGis(): Promise<void> {
  if (gisReadyPromise) return gisReadyPromise;
  gisReadyPromise = waitForGlobal(() => !!window.google?.accounts?.oauth2);
  return gisReadyPromise;
}

function readCachedToken(): string | null {
  try {
    const token = sessionStorage.getItem(TOKEN_KEY);
    const expiresAt = Number(sessionStorage.getItem(TOKEN_EXPIRY_KEY) || 0);
    if (!token || !expiresAt) return null;
    // 60s safety margin
    if (Date.now() > expiresAt - 60_000) return null;
    return token;
  } catch {
    return null;
  }
}

function cacheToken(token: string, expiresInSeconds: number) {
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(
      TOKEN_EXPIRY_KEY,
      String(Date.now() + expiresInSeconds * 1000)
    );
  } catch {
    /* ignore */
  }
}

function clearCachedToken() {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
  } catch {
    /* ignore */
  }
}

function requestNewToken(prompt: "" | "consent" = ""): Promise<string> {
  if (!CLIENT_ID) {
    return Promise.reject(
      new Error(
        "Google Client ID is not configured. Set VITE_GOOGLE_CLIENT_ID."
      )
    );
  }

  return ensureGis().then(
    () =>
      new Promise<string>((resolve, reject) => {
        try {
          const tokenClient = window.google.accounts.oauth2.initTokenClient({
            client_id: CLIENT_ID,
            scope: SCOPES,
            callback: (response: {
              access_token?: string;
              expires_in?: number;
              error?: string;
            }) => {
              if (response.error || !response.access_token) {
                reject(
                  new Error(
                    response.error || "Failed to obtain Google access token"
                  )
                );
                return;
              }
              cacheToken(response.access_token, response.expires_in ?? 3600);
              resolve(response.access_token);
            },
          });
          tokenClient.requestAccessToken({ prompt });
        } catch (err) {
          reject(err instanceof Error ? err : new Error(String(err)));
        }
      })
  );
}

/**
 * Returns a valid Drive access token. Tries cache, then silent refresh,
 * then falls back to a consent popup.
 */
export async function ensureDriveAccessToken(): Promise<string> {
  const cached = readCachedToken();
  if (cached) return cached;

  try {
    return await requestNewToken("");
  } catch {
    // Fall back to a visible consent popup
    return requestNewToken("consent");
  }
}

function escapeQueryValue(value: string): string {
  // Drive's q syntax uses single quotes; escape any in the user's keyword.
  return value.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
}

export interface SearchOptions {
  keyword: string;
  mimeType: typeof PDF_MIME | typeof DOCX_MIME;
  pageSize?: number;
}

/**
 * Search Drive for files matching `name contains <keyword>` of the given
 * mimeType. Auto-retries once on 401 with a fresh token.
 */
export async function searchDriveFiles(
  opts: SearchOptions
): Promise<DriveFile[]> {
  const trimmed = opts.keyword.trim();
  if (!trimmed) return [];

  const q = [
    `name contains '${escapeQueryValue(trimmed)}'`,
    `mimeType = '${opts.mimeType}'`,
    `trashed = false`,
  ].join(" and ");

  const params = new URLSearchParams({
    q,
    pageSize: String(opts.pageSize ?? 8),
    orderBy: "modifiedTime desc",
    fields: "files(id,name,mimeType,size,modifiedTime,iconLink)",
  });

  const url = `https://www.googleapis.com/drive/v3/files?${params.toString()}`;

  const doFetch = async (token: string) =>
    fetch(url, { headers: { Authorization: `Bearer ${token}` } });

  let token = await ensureDriveAccessToken();
  let res = await doFetch(token);

  if (res.status === 401) {
    clearCachedToken();
    token = await ensureDriveAccessToken();
    res = await doFetch(token);
  }

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Drive search failed (${res.status}): ${text}`);
  }

  const data = await res.json();
  return (data.files ?? []) as DriveFile[];
}

/**
 * Download a Drive file as a native `File` object that can be fed straight
 * into the existing upload pipeline.
 */
export async function downloadDriveFile(
  fileId: string,
  name: string,
  mimeType: string
): Promise<File> {
  const url = `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(
    fileId
  )}?alt=media`;

  const doFetch = async (token: string) =>
    fetch(url, { headers: { Authorization: `Bearer ${token}` } });

  let token = await ensureDriveAccessToken();
  let res = await doFetch(token);

  if (res.status === 401) {
    clearCachedToken();
    token = await ensureDriveAccessToken();
    res = await doFetch(token);
  }

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Drive download failed (${res.status}): ${text}`);
  }

  const blob = await res.blob();
  return new File([blob], name, { type: mimeType || blob.type });
}

export function isDriveSearchConfigured(): boolean {
  return Boolean(CLIENT_ID);
}
